import { useMatches } from "react-router-dom";

import { useAiSettings } from "../../../api/hooks";
import { useFeatureFlagQuery } from "../../../hooks/useFeatureFlag";
import { useEnhancedAiPanelEnabled } from "./useEnhancedAiPanelEnabled";
import { useFormAuthoringCoachEnabled } from "./useFormAuthoringCoachEnabled";
import { useFormQuickActionEnabled } from "./useFormQuickActionEnabled";
import { useIsAuthoringReviewForm } from "./useIsAuthoringReviewForm";

import { isInIframe } from "../../../utils/isInIframe";

import RouterTags from "../../../constants/RouterTags";
import { useAskPerformYardStore } from "../AskPerformYardStore";

/**
 * Visibility for the Ask PY top-nav trigger + docked panel: the conversational features on any page,
 * plus the Form Authoring Coach on a review the user is filling out.
 * The rollout gate is PY-12464's `useEnhancedAiPanelEnabled` (ephemeral FF + new side nav) — the same
 * signal that narrows the FAB's conversational clause, so the two conversational surfaces are never
 * both live. The coach clauses in `useAskPerformYardVisible` are deliberately outside that gate, so on
 * a goal-creation or form-builder page the FAB can still be mounted alongside this trigger; the
 * interlock in `index.tsx` hides it while the panel is open.
 */
export const useAskPyTriggerVisible = (): boolean => {
	const { value: aiEnabled } = useFeatureFlagQuery("ff-is-ai-enabled", false);
	const { value: conversationalEnabled } = useFeatureFlagQuery("ff-is-conversational-ui-enabled", false);
	const panelEnabled = useEnhancedAiPanelEnabled();
	const { data: aiSettings } = useAiSettings();

	const askHrEnabled = aiSettings?.conversationalAiSettings?.askHrEnabled ?? false;
	const goalCreationEnabled = aiSettings?.conversationalAiSettings?.goalCreationEnabled ?? false;
	// Shared with the Forms quick-action card, so the trigger never opens a panel with an empty card grid.
	const formCreationEnabled = useFormQuickActionEnabled();
	// The Form Authoring Coach is a capability in its own right (PY-13060): a company can enable it
	// without buying any of the conversational features above, and the coach's callout and quick-action
	// card both render inside this trigger. Without this term they would be unreachable at such a
	// company. Page-scoped, since off a review the user is authoring the coach has nothing to evaluate.
	const formAuthoringCoachEnabled = useFormAuthoringCoachEnabled();
	const isAuthoringReviewForm = useIsAuthoringReviewForm();

	const matches = useMatches();
	const tags: string[] =
		matches.length > 0 ? ((matches[matches.length - 1]?.handle as { tags?: string[] })?.tags ?? []) : [];
	const hiddenByRoute = tags.includes(RouterTags.NO_ASK_PERFORMYARD);
	const forceHidden = useAskPerformYardStore((state) => state.forceHidden);

	return (
		panelEnabled &&
		aiEnabled &&
		conversationalEnabled &&
		!isInIframe() &&
		!hiddenByRoute &&
		!forceHidden &&
		(askHrEnabled ||
			goalCreationEnabled ||
			formCreationEnabled ||
			(isAuthoringReviewForm && formAuthoringCoachEnabled))
	);
};
